import styled from '@emotion/styled';
import { getImageAsBase64 } from '@hotjar/feedback/components/Surveys/utils';
import { Button, Stack, tokens, Field, PlanBadge } from '@hotjar/hotsauce';
import { useGate, useTierForGate } from '@hotjar/allowances';
import type { ChangeEvent } from 'react';
import { useEffect, useCallback, useState, useRef } from 'react';
import { useCurrentSite } from '@hotjar/site';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { useTranslation } from '@hotjar/translations';

/* Required because of bootstrap overriding display block to input */
const StyledInput = styled.input`
  display: none !important;
`;

const StyledLogo = styled.img`
  display: block;
  max-width: 100%;
  margin-top: ${tokens.spacer.x1};
  max-height: 120px;
`;

const ButtonContainer = styled(Stack)`
  margin-top: ${tokens.spacer.x1};
  align-items: center;
`;

type ErrorMessageKey = 'load' | 'size' | 'format';
const errorMessageKeys: ErrorMessageKey[] = ['load', 'size', 'format'];

const MAX_FILE_SIZE = 2 * 1024 ** 2; // 2 MB
const ACCEPTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

const validateFile = (file: File | undefined) => {
  if (file === undefined) throw new Error('load');
  if (file.size > MAX_FILE_SIZE) throw new Error('size');
  if (!ACCEPTED_FORMATS.includes(file.type)) throw new Error('format');
};

export interface LogoSectionProps {
  logoSource: string;
  error: string | null;
  onChange: (newValue: string) => void;
  onRemoveLogo: () => void;
}

export function LogoSection({
  logoSource,
  error,
  onChange,
  onRemoveLogo,
}: LogoSectionProps): JSX.Element {
  const { t } = useTranslation('feedback', { keyPrefix: 'wizard.section.appearance' });
  const inputRef = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState<string | undefined>(error ?? undefined);

  const { data: site } = useCurrentSite();
  const currentSiteId = site && site.id ? `${site.id}` : null;

  const planRequiredForLogo = useTierForGate('feedback.survey_question_images', {
    id: currentSiteId,
    type: 'site',
  });
  const hasLogoAllowance = useGate('feedback.can_hide_branding', {
    id: `${site?.id}`,
    type: 'site',
  });

  const hasPlanBadge = hasLogoAllowance === true ? null : planRequiredForLogo;

  const showUpsell = useCallback(() => {
    if (site) {
      upsell({ referrer: 'survey.appearance.logo', site });
    }
  }, [site]);

  const handleLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target?.files?.[0] === undefined) {
      return;
    }
    setLogoError(undefined);

    try {
      const image = event.target.files[0];
      validateFile(image);

      const encodedImage = await getImageAsBase64(image);
      onChange(encodedImage);
    } catch (err) {
      const messageKey =
        err instanceof Error && errorMessageKeys.includes(err.message as ErrorMessageKey)
          ? err.message
          : 'load';
      const errorMessage = t(`logo-${messageKey}-error`);
      setLogoError(errorMessage);
    }
  };

  const handleButtonClick = () => {
    if (logoSource) {
      return onRemoveLogo();
    }

    if (!(hasLogoAllowance ?? false)) {
      return showUpsell();
    }

    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  useEffect(() => {
    setLogoError(error ?? undefined);
  }, [error]);

  return (
    <Field
      inputId="logo-section"
      labelText={t('logo-title')}
      helperText={t('logo-rescale')}
      invalidText={logoError}
      isInvalid={logoError !== undefined}
      size="l"
    >
      <>
        {logoSource && <StyledLogo src={logoSource} alt={t('logo-title')} />}
        <ButtonContainer spacing="x1">
          <Button
            kind="secondary"
            icon={logoSource ? 'trash' : 'image'}
            size="s"
            onClick={handleButtonClick}
          >
            {logoSource ? t('logo-remove') : t('logo-upload')}
          </Button>
          {!logoSource && hasPlanBadge && (
            <PlanBadge size="s" kind={hasPlanBadge} onClick={showUpsell} />
          )}
        </ButtonContainer>
        <StyledInput
          type="file"
          ref={inputRef}
          onChange={handleLogoChange}
          accept=".png, .gif, .jpg, .jpeg, .webp"
          data-testid="logo-section-input"
        />
      </>
    </Field>
  );
}
